import type { Bilingual } from '../bilingual.js';
import { useBilingual } from '../locale-provider.js';

/* Copy dos componentes de admin-push de notificações (ADR-0687): modal de composição e tabela.
   Nomeado `notifications-admin` (não `notifications`) para não colidir com um eventual conteúdo
   de notificações in-app do usuário final, que é um domínio diferente. */
interface NotificationsAdminContent {
  readonly composeModal: {
    readonly title: string;
    readonly validationError: string;
    readonly createErrorToast: string;
    readonly createdToast: string;
    readonly titleLabel: string;
    readonly bodyLabel: string;
    readonly severityLabel: string;
    readonly severityInfo: string;
    readonly severitySuccess: string;
    readonly severityWarning: string;
    readonly severityError: string;
    readonly audienceLabel: string;
    readonly audienceAll: string;
    readonly audienceUsers: string;
    readonly userIdsLabel: string;
    readonly actionLabel: string;
    readonly expiresLabel: string;
    readonly cancel: string;
    readonly submit: string;
  };
  readonly table: {
    readonly emptyFilteredTitle: string;
    readonly emptyFilteredDescription: string;
    readonly clearFilters: string;
    readonly emptyTitle: string;
    readonly emptyDescription: string;
    readonly compose: string;
    readonly columnNotification: string;
    readonly columnSeverity: string;
    readonly columnStatus: string;
    readonly columnAudience: string;
    readonly columnActions: string;
    readonly published: string;
    readonly draft: string;
    readonly audienceAll: string;
    readonly audienceUsers: (count: number) => string;
    readonly publish: string;
    readonly remove: string;
  };
}

const NOTIFICATIONS_ADMIN_CONTENT: Bilingual<NotificationsAdminContent> = {
  'pt-BR': {
    composeModal: {
      title: 'Nova notificação',
      validationError: 'Verifique os campos (título obrigatório; usuários exige ao menos 1 id).',
      createErrorToast: 'Falha ao criar a notificação.',
      createdToast: 'Notificação criada (rascunho). Publique para enviar.',
      titleLabel: 'Título',
      bodyLabel: 'Mensagem',
      severityLabel: 'Severidade',
      severityInfo: 'Informação',
      severitySuccess: 'Sucesso',
      severityWarning: 'Aviso',
      severityError: 'Erro',
      audienceLabel: 'Público',
      audienceAll: 'Todos os usuários',
      audienceUsers: 'Usuários selecionados',
      userIdsLabel: 'Ids dos usuários (separados por vírgula/espaço)',
      actionLabel: 'Rota (opcional)',
      expiresLabel: 'Expira em (opcional)',
      cancel: 'Cancelar',
      submit: 'Criar rascunho',
    },
    table: {
      emptyFilteredTitle: 'Nenhum resultado',
      emptyFilteredDescription: 'Nenhuma notificação corresponde aos filtros.',
      clearFilters: 'Limpar filtros',
      emptyTitle: 'Nenhuma notificação',
      emptyDescription: 'Componha a primeira para os usuários.',
      compose: 'Nova notificação',
      columnNotification: 'Notificação',
      columnSeverity: 'Severidade',
      columnStatus: 'Status',
      columnAudience: 'Público',
      columnActions: 'Ações',
      published: 'Publicada',
      draft: 'Rascunho',
      audienceAll: 'Todos',
      audienceUsers: (count) => `${count} usuário(s)`,
      publish: 'Publicar',
      remove: 'Remover',
    },
  },
  'en-US': {
    composeModal: {
      title: 'New notification',
      validationError: 'Check the fields (title required; users requires at least 1 id).',
      createErrorToast: 'Failed to create the notification.',
      createdToast: 'Notification created (draft). Publish to send it.',
      titleLabel: 'Title',
      bodyLabel: 'Message',
      severityLabel: 'Severity',
      severityInfo: 'Info',
      severitySuccess: 'Success',
      severityWarning: 'Warning',
      severityError: 'Error',
      audienceLabel: 'Audience',
      audienceAll: 'All users',
      audienceUsers: 'Selected users',
      userIdsLabel: 'User ids (comma/space separated)',
      actionLabel: 'Route (optional)',
      expiresLabel: 'Expires at (optional)',
      cancel: 'Cancel',
      submit: 'Create draft',
    },
    table: {
      emptyFilteredTitle: 'No results',
      emptyFilteredDescription: 'No notification matches the filters.',
      clearFilters: 'Clear filters',
      emptyTitle: 'No notifications',
      emptyDescription: 'Compose the first one for your users.',
      compose: 'New notification',
      columnNotification: 'Notification',
      columnSeverity: 'Severity',
      columnStatus: 'Status',
      columnAudience: 'Audience',
      columnActions: 'Actions',
      published: 'Published',
      draft: 'Draft',
      audienceAll: 'All',
      audienceUsers: (count) => `${count} user(s)`,
      publish: 'Publish',
      remove: 'Remove',
    },
  },
};

export const useNotificationsAdminContent = (): NotificationsAdminContent =>
  useBilingual(NOTIFICATIONS_ADMIN_CONTENT);
